import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, shareReplay, map } from 'rxjs/operators';
import { GetUserBuildingGQL } from 'src/graphql/generated/schema';

export interface BuildingData {
  id: string;
  name?: string | null;
  address?: string | null;
  buildingCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  propertyManagerEmail?: string | null;
  propertyManagerPhone?: string | null;
  propertyManagerCompany?: string | null;
  lightingZones?: any[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class BuildingStateService {
  private buildingSubject = new BehaviorSubject<BuildingData | null>(null);
  private buildingCache$?: Observable<BuildingData | null>;

  public building$ = this.buildingSubject.asObservable();

  constructor(private getUserBuildingGQL: GetUserBuildingGQL) {}

  /**
   * Load building data for the user
   */
  loadBuilding(): void {
    // console.log('[BuildingStateService] loadBuilding() called');

    if (this.buildingCache$) {
      // console.log('[BuildingStateService] Using cached building data');
      this.buildingCache$.subscribe();
      return;
    }

    this.buildingCache$ = this.getUserBuildingGQL.fetch().pipe(
      map((result: any) => result.data?.getUserBuilding as BuildingData | null),
      tap(building => {
        // console.log('[BuildingStateService] Building data loaded:', building);
        this.buildingSubject.next(building);
      }),
      shareReplay(1) // Cache the result
    );

    this.buildingCache$.subscribe();
  }

  /**
   * Get the current building data (synchronous)
   */
  getCurrentBuilding(): BuildingData | null {
    return this.buildingSubject.value;
  }

  /**
   * Clear cached building data (useful for forcing a refresh)
   */
  clearCache(): void {
    this.buildingCache$ = undefined;
    this.buildingSubject.next(null);
  }
}
